import React from 'react';
import { Card, Icon, Header } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { colorsModeUI } from '../modeUI';
import './index.css';
import {
  SECONDARY_COLOR,
  ALARM_COLOR,
  ALARM_COLOR_INVERTED,
  POSITIVE_COLOR,
  POSITIVE_COLOR_INVERTED,
  BLACK_COLOR,
  ICON_COLOR,
  WARNING_COLOR,
  WHITE_COLOR,
  DISABLE_COLOR,
} from '../colors';

import {
  TYPE_POSITIVE,
  TYPE_NEGATIVE,
  TYPE_INFORMATION,
  TYPE_WARNING,
  TYPE_DISABLED,
} from '../constants';

/**
 * BoxSingleData
 * this class creates a single display component inside a box (card) to show a title
 * and a single value
 * Parameters (Props):
 * @param string title
 * @param string value: if the value has more as 33 characters, then it will be cut
 * @param string titleValue
 * @param string unitValue
 * @param string type: [TYPE_POSITIVE, TYPE_NEGATIVE, TYPE_INFORMATION, TYPE_WARNING, TYPE_DISABLED]
 * @param func onClick: On click handler for the whole surface (optional).
 * @param bool showIconUpdate: if this is true, then it will be shown a update icon
 * @param func onClickIconUpdate: this function will be called by click the icon update
 * @param string mode: defines the mode UI [MODE_DARK, MODE_LIGHT]
 * @param array icons: array with icons to be shown at top right (near title)
 * @param string titleAlarm: the name of the alarm to be displayed together
 *  with the icon warning sign (if present)
 */
export default class BoxSingleData extends React.PureComponent {
  constructor(props) {
    super(props);
    this.onClick = this.onClick.bind(this);
    this.state = {
      clickable: !!props.onClick,
    };
  }

  /**
   * OnClick
   * @param object event
   * @param object data
   */
  onClick(event, data) {
    const { onClick } = this.props;
    if (onClick) {
      onClick(event, data);
    }
  }

  render() {
    const { clickable } = this.state;
    const {
      title,
      value,
      titleValue,
      unitValue,
      showIconUpdate,
      onClickIconUpdate,
      mode,
      icons,
      titleAlarm,
    } = this.props;
    let { type } = this.props;
    if (
      type !== TYPE_POSITIVE &&
      type !== TYPE_NEGATIVE &&
      type !== TYPE_WARNING &&
      type !== TYPE_DISABLED
    ) {
      type = TYPE_INFORMATION;
    }

    /* default colors used when the mode UI is not defined */
    let colors = {
      [TYPE_DISABLED]: {
        headerBackgroundColor: SECONDARY_COLOR,
        headerColor: BLACK_COLOR,
        valueColor: DISABLE_COLOR,
        backgroundColor: WHITE_COLOR,
        topBarBox: SECONDARY_COLOR,
      },
      [TYPE_INFORMATION]: {
        headerBackgroundColor: SECONDARY_COLOR,
        headerColor: BLACK_COLOR,
        valueColor: BLACK_COLOR,
        backgroundColor: WHITE_COLOR,
        topBarBox: SECONDARY_COLOR,
      },
      [TYPE_POSITIVE]: {
        headerBackgroundColor: POSITIVE_COLOR,
        headerColor: POSITIVE_COLOR_INVERTED,
        valueColor: POSITIVE_COLOR,
        backgroundColor: WHITE_COLOR,
        topBarBox: POSITIVE_COLOR,
      },
      [TYPE_NEGATIVE]: {
        headerBackgroundColor: ALARM_COLOR,
        headerColor: ALARM_COLOR_INVERTED,
        valueColor: ALARM_COLOR,
        backgroundColor: WHITE_COLOR,
        topBarBox: ALARM_COLOR,
      },
      [TYPE_WARNING]: {
        headerBackgroundColor: WARNING_COLOR,
        headerColor: SECONDARY_COLOR,
        valueColor: WARNING_COLOR,
        backgroundColor: WHITE_COLOR,
        topBarBox: WARNING_COLOR,
      },
    };

    /* mode UI: dark mode/light mode */
    if (mode !== null) {
      const modeColors = colorsModeUI(mode);
      colors = {
        [TYPE_DISABLED]: {
          headerBackgroundColor: modeColors.BOX_HEADER_BACKGROUND_COLOR,
          headerColor: modeColors.BOX_HEADER_COLOR,
          valueColor: modeColors.DISABLE_COLOR,
          backgroundColor: modeColors.BOX_BACKGROUND_COLOR,
          topBarBox: modeColors.BOX_HEADER_TOP_BAR_DISABLED_COLOR,
        },
        [TYPE_INFORMATION]: {
          headerBackgroundColor: modeColors.BOX_HEADER_BACKGROUND_COLOR,
          headerColor: modeColors.BOX_HEADER_COLOR,
          valueColor: modeColors.INFORMATION_COLOR,
          backgroundColor: modeColors.BOX_BACKGROUND_COLOR,
          topBarBox: modeColors.BOX_HEADER_BACKGROUND_COLOR,
        },
        [TYPE_POSITIVE]: {
          headerBackgroundColor: modeColors.BOX_HEADER_BACKGROUND_COLOR,
          headerColor: modeColors.BOX_HEADER_COLOR,
          valueColor: modeColors.POSITIVE_COLOR,
          backgroundColor: modeColors.BOX_BACKGROUND_COLOR,
          topBarBox: modeColors.POSITIVE_COLOR,
        },
        [TYPE_NEGATIVE]: {
          headerBackgroundColor: modeColors.BOX_HEADER_BACKGROUND_COLOR,
          headerColor: modeColors.BOX_HEADER_COLOR,
          valueColor: modeColors.ALARM_COLOR,
          backgroundColor: modeColors.BOX_BACKGROUND_COLOR,
          topBarBox: modeColors.ALARM_COLOR,
        },
        [TYPE_WARNING]: {
          headerBackgroundColor: modeColors.BOX_HEADER_BACKGROUND_COLOR,
          headerColor: modeColors.BOX_HEADER_COLOR,
          valueColor: modeColors.WARNING_COLOR,
          backgroundColor: modeColors.BOX_BACKGROUND_COLOR,
          topBarBox: modeColors.WARNING_COLOR,
        },
      };
    }
    const cardProps = {};
    if (clickable) {
      cardProps.onClick = this.onClick;
    }

    return (
      <Card className="box-single-data" {...cardProps} centered>
        <div
          className="top-bar-box"
          style={{ border: 'none', backgroundColor: colors[type].topBarBox }}
        />
        <Card.Content
          textAlign="center"
          className="box-header"
          style={{
            backgroundColor: colors[type].headerBackgroundColor,
            border: 'none',
          }}
        >
          <div className="title-buttons">
            <div>
              <Header
                as="h4"
                textAlign="left"
                style={{ color: colors[type].headerColor }}
              >
                {title}
              </Header>
            </div>
            <div className="buttons">
              {icons && icons.length > 0 && (
                <div>{icons.map((icon) => icon.button)}</div>
              )}
            </div>
          </div>
        </Card.Content>
        <Card.Content
          textAlign="center"
          style={{
            color: colors[type].valueColor,
            backgroundColor: colors[type].backgroundColor,
            borderTopColor: colors[type].backgroundColor,
          }}
        >
          <div className="title-value-and-button">
            <div className="title-value">{titleValue}</div>
            {showIconUpdate && (
              <div className="update-button" style={{ color: ICON_COLOR }}>
                <Icon link name="write" onClick={onClickIconUpdate} />
              </div>
            )}
          </div>
          {titleAlarm && (
            <div className="icon-alarm-wrapper">
              <div className="icon-alarm">
                <Icon
                  name="warning sign"
                  style={{ color: colors[type].valueColor }}
                  size="small"
                  key="alarms"
                />
              </div>
              <div className="title-alarm">{titleAlarm}</div>
            </div>
          )}
          <div className="value-unit">
            <div className="value">
              {value.length > 33 ? `${value.substr(0, 32)}...` : value}
            </div>
            <div className="unit">{unitValue}</div>
          </div>
        </Card.Content>
      </Card>
    );
  }
}

BoxSingleData.defaultProps = {
  onClick: null,
  onClickIconUpdate: null,
  showIconUpdate: false,
  mode: null,
  titleValue: null,
  unitValue: null,
  icons: null,
  titleAlarm: null,
};

BoxSingleData.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  titleValue: PropTypes.string,
  unitValue: PropTypes.string,
  type: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  showIconUpdate: PropTypes.bool,
  onClickIconUpdate: PropTypes.func,
  mode: PropTypes.string,
  icons: PropTypes.arrayOf(Object),
  titleAlarm: PropTypes.string,
};
